/**
 * Deterministic simulation step.
 *
 * Engine-free and pure w.r.t. the World (no Babylon, no Date.now, no
 * Math.random — randomness comes from `world.rng`). The same code runs as
 * server authority and client prediction.
 *
 * Systems (fixed order each tick): bot AI → movement (+cover, +arena clamp) →
 * firing → projectile integration/collision → pickups. All character stats and
 * weapons are data-driven (CHARACTERS); obstacles come from the active arena.
 */
import {
  ARENA_HALF_EXTENT,
  FIXED_DT,
  CHARACTERS,
  BOT_AI,
  PICKUP,
  ARENAS,
  weaponRange,
  type CharacterDef,
} from "../constants/index.js";
import {
  add,
  sub,
  scale,
  length,
  distance,
  normalize,
  clampLength,
  directionTo,
  rotate,
  circleVsCircle,
  resolveCircleAabb,
  segmentVsAabb,
  segmentVsCircle,
  type Vec2,
  type Aabb,
} from "../math/vec2.js";
import { nextFloat } from "../math/rng.js";
import {
  withTransform,
  withKind,
  withProjectile,
  spawnEntity,
  destroyEntity,
  type World,
  type EntityId,
  type EntityKind,
} from "../ecs/index.js";

const ZERO: Vec2 = { x: 0, y: 0 };

const obstaclesOf = (world: World): readonly Aabb[] => ARENAS[world.arenaId].obstacles;

const charOf = (world: World, id: EntityId): CharacterDef | undefined => {
  const cid = world.character[id];
  return cid ? CHARACTERS[cid] : undefined;
};

const radiusOf = (world: World, id: EntityId): number => {
  if (world.kind[id] === "projectile") return world.projectile[id]?.radius ?? 0.22;
  return charOf(world, id)?.radius ?? 0.5;
};

const isDead = (world: World, id: EntityId): boolean => {
  const h = world.health[id];
  return h !== undefined && h.current <= 0;
};

const isEnemy = (a: EntityKind | undefined, b: EntityKind | undefined): boolean =>
  (a === "player" && b === "bot") || (a === "bot" && b === "player");

const nearestEnemy = (world: World, selfId: EntityId): EntityId | null => {
  const st = world.transform[selfId];
  const sk = world.kind[selfId];
  if (!st) return null;
  let best = Infinity;
  let result: EntityId | null = null;
  for (const id of withTransform(world)) {
    if (!isEnemy(sk, world.kind[id]) || isDead(world, id)) continue;
    const t = world.transform[id];
    if (!t) continue;
    const d = distance(st.pos, t.pos);
    if (d < best) {
      best = d;
      result = id;
    }
  }
  return result;
};

const blockedSeg = (obstacles: readonly Aabb[], a: Vec2, b: Vec2): boolean =>
  obstacles.some((ob) => segmentVsAabb(a, b, ob));

const hasLineOfSight = (world: World, a: Vec2, b: Vec2): boolean =>
  !blockedSeg(obstaclesOf(world), a, b);

const clampAxis = (v: number, radius: number): number => {
  const limit = ARENA_HALF_EXTENT - radius;
  return Math.max(-limit, Math.min(limit, v));
};

const outOfArena = (p: Vec2): boolean =>
  Math.abs(p.x) > ARENA_HALF_EXTENT || Math.abs(p.y) > ARENA_HALF_EXTENT;

// --- bot AI helpers -------------------------------------------------------

/** Steer `desired` around cover: rotate until a short lookahead is clear. */
const avoidObstacles = (obstacles: readonly Aabb[], pos: Vec2, desired: Vec2): Vec2 => {
  if (length(desired) < 1e-4) return ZERO;
  const look = BOT_AI.avoidLookahead;
  if (!blockedSeg(obstacles, pos, add(pos, scale(desired, look)))) return desired;
  for (const a of [0.4, -0.4, 0.8, -0.8, 1.2, -1.2, 1.8, -1.8]) {
    const d = rotate(desired, a);
    if (!blockedSeg(obstacles, pos, add(pos, scale(d, look)))) return d;
  }
  // Boxed in: slide perpendicular so we never pin straight into a wall.
  return rotate(desired, Math.PI / 2);
};

/** Is an enemy projectile close enough to count as an active threat? */
const enemyProjectileNear = (world: World, selfId: EntityId, pos: Vec2): boolean => {
  const sk = world.kind[selfId];
  for (const id of withProjectile(world)) {
    const pr = world.projectile[id];
    const pt = world.transform[id];
    if (!pr || !pt) continue;
    if (!isEnemy(sk, world.kind[pr.owner])) continue;
    if (distance(pt.pos, pos) < BOT_AI.threatDist) return true;
  }
  return false;
};

/** A point tucked behind the nearest obstacle that breaks LoS to the target. */
const coverPointAwayFrom = (
  world: World,
  from: Vec2,
  target: Vec2,
): Vec2 | null => {
  let best: Vec2 | null = null;
  let bestDist = Infinity;
  for (const ob of obstaclesOf(world)) {
    const center = { x: (ob.min.x + ob.max.x) / 2, y: (ob.min.y + ob.max.y) / 2 };
    const half = Math.max(ob.max.x - ob.min.x, ob.max.y - ob.min.y) / 2;
    const away = directionTo(target, center);
    const behind = add(center, scale(away, half + 1.2));
    if (hasLineOfSight(world, behind, target)) continue; // doesn't actually hide
    const d = distance(from, behind);
    if (d < bestDist) {
      bestDist = d;
      best = behind;
    }
  }
  return best;
};

// --- systems --------------------------------------------------------------

const updateBotAI = (world: World): void => {
  const obstacles = obstaclesOf(world);
  for (const id of withKind(world, "bot")) {
    const bt = world.transform[id];
    const intent = world.intent[id];
    const def = charOf(world, id);
    if (!bt || !intent) continue;
    if (isDead(world, id) || !def) {
      if (intent) {
        intent.move = ZERO;
        intent.fire = false;
      }
      continue;
    }
    const targetId = nearestEnemy(world, id);
    const tt = targetId !== null ? world.transform[targetId] : undefined;
    if (!tt) {
      intent.move = ZERO;
      intent.fire = false;
      continue;
    }

    const toTarget = sub(tt.pos, bt.pos);
    const dist = length(toTarget);
    const dirToTarget = normalize(toTarget);
    const los = hasLineOfSight(world, bt.pos, tt.pos);
    const hp = world.health[id];
    const lowHealth = hp ? hp.current / hp.max < BOT_AI.lowHealthFrac : false;

    // Radial kiting: chase if no LoS or too far; back off if too close.
    let radial: Vec2 = ZERO;
    if (!los || dist > BOT_AI.bandMax) radial = dirToTarget;
    else if (dist < BOT_AI.bandMin) radial = scale(dirToTarget, -1);

    // Strafe perpendicular; sign flips slowly and is desynced per bot.
    const sign = (Math.floor(world.tick / BOT_AI.strafeFlipTicks) + id) % 2 === 0 ? 1 : -1;
    const strafeW = enemyProjectileNear(world, id, bt.pos) ? 1 : BOT_AI.strafeBias;
    const perp = scale(rotate(dirToTarget, Math.PI / 2), sign * strafeW);

    let desired = add(radial, perp);
    if (lowHealth) {
      const hide = coverPointAwayFrom(world, bt.pos, tt.pos);
      if (hide) desired = directionTo(bt.pos, hide);
    }
    desired = length(desired) > 1e-4 ? normalize(desired) : ZERO;
    intent.move = avoidObstacles(obstacles, bt.pos, desired);

    // Aim: lead the moving target, plus a small skill-limited error.
    const projSpeed = def.weapon.projectile.speed;
    const tHit = projSpeed > 0 ? dist / projSpeed : 0;
    const predicted = add(tt.pos, scale(tt.vel, tHit));
    const err = (nextFloat(world.rng) - 0.5) * BOT_AI.aimErrorRad;
    intent.aim = rotate(directionTo(bt.pos, predicted), err);

    const range = Math.min(weaponRange(def) * 0.95, BOT_AI.sightRange);
    intent.fire = los && dist <= range && !lowHealth;
  }
};

const updateMovement = (world: World): void => {
  const obstacles = obstaclesOf(world);
  for (const id of withTransform(world)) {
    if (world.kind[id] === "projectile") continue;
    const t = world.transform[id];
    if (!t) continue;
    if (isDead(world, id)) {
      t.vel = ZERO;
      continue;
    }
    const intent = world.intent[id];
    const speed = charOf(world, id)?.moveSpeed ?? 5;
    const move = intent ? clampLength(intent.move, 1) : ZERO;
    t.vel = scale(move, speed);

    let next = add(t.pos, scale(t.vel, FIXED_DT));
    const r = radiusOf(world, id);
    for (const ob of obstacles) next = resolveCircleAabb(next, r, ob);
    t.pos = { x: clampAxis(next.x, r), y: clampAxis(next.y, r) };
  }
};

const fireProjectile = (world: World, shooterId: EntityId): void => {
  const st = world.transform[shooterId];
  const intent = world.intent[shooterId];
  const def = charOf(world, shooterId);
  if (!st || !intent || !def) return;
  const w = def.weapon;

  let baseDir = intent.aim;
  if (length(baseDir) < 0.001) {
    const target = nearestEnemy(world, shooterId);
    const tt = target !== null ? world.transform[target] : undefined;
    baseDir = tt ? directionTo(st.pos, tt.pos) : { x: 1, y: 0 };
  } else {
    baseDir = normalize(baseDir);
  }

  const offset = radiusOf(world, shooterId) + w.projectile.radius + 0.05;
  for (let i = 0; i < w.pellets; i++) {
    let angle = 0;
    if (w.pellets > 1) {
      const t = i / (w.pellets - 1) - 0.5; // -0.5 .. 0.5 across the cone
      angle = t * w.spreadRad + (nextFloat(world.rng) - 0.5) * (w.spreadRad / w.pellets);
    }
    const dir = rotate(baseDir, angle);
    const pid = spawnEntity(world, "projectile");
    world.transform[pid] = {
      pos: add(st.pos, scale(dir, offset)),
      vel: scale(dir, w.projectile.speed),
    };
    world.projectile[pid] = {
      owner: shooterId,
      damage: w.projectile.damage,
      ttl: w.projectile.ttlTicks,
      radius: w.projectile.radius,
    };
  }
};

const updateFiring = (world: World): void => {
  for (const id of withTransform(world)) {
    const wp = world.weapon[id];
    const def = charOf(world, id);
    if (!wp || !def) continue;
    if (wp.cooldown > 0) wp.cooldown -= 1;
    const intent = world.intent[id];
    if (!intent || !intent.fire || isDead(world, id) || wp.cooldown > 0) continue;
    fireProjectile(world, id);
    wp.cooldown = def.weapon.cooldownTicks;
  }
};

const updateProjectiles = (world: World): void => {
  const obstacles = obstaclesOf(world);
  for (const id of withProjectile(world)) {
    const t = world.transform[id];
    const pr = world.projectile[id];
    if (!t || !pr) continue;

    const from = t.pos;
    const to = add(t.pos, scale(t.vel, FIXED_DT));

    let consumed = blockedSeg(obstacles, from, to);
    if (!consumed) {
      for (const cid of withTransform(world)) {
        if (cid === pr.owner) continue;
        const ck = world.kind[cid];
        if (ck !== "player" && ck !== "bot") continue;
        if (isDead(world, cid)) continue;
        const ct = world.transform[cid];
        if (!ct) continue;
        if (segmentVsCircle(from, to, ct.pos, radiusOf(world, cid) + pr.radius)) {
          const h = world.health[cid];
          if (h) h.current = Math.max(0, h.current - pr.damage);
          consumed = true;
          break;
        }
      }
    }

    if (consumed) {
      destroyEntity(world, id);
      continue;
    }
    t.pos = to;
    pr.ttl -= 1;
    if (pr.ttl <= 0 || outOfArena(t.pos)) destroyEntity(world, id);
  }
};

const updatePickups = (world: World): void => {
  const characters = [...withKind(world, "player"), ...withKind(world, "bot")];
  for (const slot of world.pickups) {
    if (slot.cooldown > 0) {
      slot.cooldown -= 1;
      continue;
    }
    for (const id of characters) {
      if (isDead(world, id)) continue;
      const t = world.transform[id];
      const h = world.health[id];
      if (!t || !h || h.current >= h.max) continue;
      if (circleVsCircle(t.pos, radiusOf(world, id), slot.pos, PICKUP.radius)) {
        h.current = Math.min(h.max, h.current + PICKUP.heal);
        slot.cooldown = PICKUP.respawnTicks;
        break; // one heal per slot per tick
      }
    }
  }
};

/** Advance the world by one fixed timestep. Mutates and returns `world`. */
export const step = (world: World): World => {
  updateBotAI(world);
  updateMovement(world);
  updateFiring(world);
  updateProjectiles(world);
  updatePickups(world);
  world.tick++;
  return world;
};
